import "./Cvv.css";

import {
  sendToControllerIframe,
  getCVVMaxLength,
  validateCVV,
  formatCvv,
} from "../../utils/";
import { handleCustomInputStyle } from "../../utils/stylesheet";
import {
  VALIDATION_STATUSES,
  ELEMENTS,
  EVENTS,
  ELEMENT_FRAMES,
  FRAMES_MODES,
} from "../../constants/";
import {
  getNewValidationStatus,
  onMessage,
  registerFocusHandler,
  setOnMouseOverClasses,
  setOnMouseOutClasses,
  setOnBlurClasses,
  setOnFocusClasses,
  fixIE11InputCursor,
  wrapOnInputForIE11,
  wrapOnInputForCompositionText,
  onHiddenInputChange,
  onAutofillInput,
  simulateAutofill,
  onBackwardNavInputFocus,
  onForwardNavInputFocus,
  onIframeWindowFocus,
  onIframeWindowBlur,
  parseAndStyleEnvironment,
  setInputManualUseOnly,
  isCvvValid,
  setAriaInvalidAttribute,
} from "../shared";
import { hasValue } from "../../utils/validation";

class Cvv {
  state = {
    scheme: "",
    validationStatus: VALIDATION_STATUSES.EMPTY,
    value: "",
  };

  previousState;
  input;

  copyPasteDisabled = false;

  constructor(env) {
    this.input = document.getElementById("checkout-frames-cvv");

    const { MODES } = parseAndStyleEnvironment(env);
    this.copyPasteDisabled = hasValue(MODES, FRAMES_MODES.DISABLE_COPY_PASTE);

    if (hasValue(MODES, FRAMES_MODES.RIGHT_TO_LEFT)) {
      this.input.parentNode.setAttribute("dir", "rtl");
    }

    this.registerEventHandlers();
    this.previousState = { ...this.state };

    sendToControllerIframe(ELEMENTS.CVV, { event: EVENTS.FRAME_ACTIVATED });
  }

  registerEventHandlers() {
    this.input.addEventListener("mouseover", this.onMouseOver);
    this.input.addEventListener("mouseout", this.onMouseOut);
    registerFocusHandler(this.input, this.onFocus);
    this.input.addEventListener(
      "input",
      wrapOnInputForIE11(wrapOnInputForCompositionText(this.onInputChange))
    );
    this.input.addEventListener("compositionend", this.onInputChange);
    this.input.addEventListener("blur", this.onBlur);

    if (this.copyPasteDisabled) {
      setInputManualUseOnly(this.input);
    }

    document
      .getElementById("cc-number-hidden")
      .addEventListener("input", onHiddenInputChange);
    document
      .getElementById("cc-exp-hidden")
      .addEventListener("input", onHiddenInputChange);

    window.addEventListener("message", onMessage("cvv", this.onReceiveMessage));

    this.backwardNavInput = document.getElementById("backward-nav-input");
    if (this.backwardNavInput) {
      this.backwardNavInput.addEventListener(
        "focus",
        onBackwardNavInputFocus(ELEMENT_FRAMES.CVV)
      );
    }

    this.forwardNavInput = document.getElementById("forward-nav-input");
    if (this.forwardNavInput) {
      this.forwardNavInput.addEventListener(
        "focus",
        onForwardNavInputFocus(ELEMENT_FRAMES.CVV)
      );
    }

    window.addEventListener(
      "focus",
      onIframeWindowFocus(
        this.input,
        this.backwardNavInput,
        this.forwardNavInput
      )
    );
    window.addEventListener(
      "blur",
      onIframeWindowBlur(this.backwardNavInput, this.forwardNavInput)
    );
  }

  updateValidationStatus = () => {
    this.previousState.validationStatus = this.state.validationStatus;

    this.state.validationStatus = getNewValidationStatus(
      this.input.value,
      isCvvValid,
      this.state.value,
      this.state.scheme
    );
  };

  /**
   * Cvv change handler
   * Format input & update the state (value, validity)
   */
  onInputChange = (event) => {
    const currentScheme = this.state.scheme;
    const formattedValue = formatCvv(event.target.value, currentScheme);

    this.state.value = formattedValue;
    this.input.value = formattedValue;
    this.updateValidationStatus();

    onAutofillInput(event);

    sendToControllerIframe(ELEMENTS.CVV, {
      event: EVENTS.VALUE_CHANGED,
      value: this.state.value,
      validationStatus: this.state.validationStatus,
      previousValidationStatus: this.previousState.validationStatus,
    });

    fixIE11InputCursor(this.input);
  };

  onMouseOver = () => {
    setOnMouseOverClasses(this.input, "cvv");
  };

  onMouseOut = () => {
    setOnMouseOutClasses(this.input, "cvv");
  };

  /**
   * Handler when the cvv input obtains focus
   */
  onFocus = () => {
    sendToControllerIframe(ELEMENTS.CVV, {
      event: EVENTS.FRAME_FOCUS,
      validationStatus: this.state.validationStatus,
    });

    setOnFocusClasses(this.input, "cvv");
  };

  /**
   * Handler when the cvv input loses focus
   */
  onBlur = () => {
    sendToControllerIframe(ELEMENTS.CVV, {
      event: EVENTS.FRAME_BLUR,
      validationStatus: this.state.validationStatus,
    });
    setAriaInvalidAttribute(this.input, this.state.validationStatus);
    setOnBlurClasses(this.input, "cvv", this.state);
    this.input.classList.remove(`cvv--focus`);
  };

  onReceiveMessage = (e) => {
    const { message, style, value } = e.data.data;

    switch (message) {
      case "initial-config": {
        if (style) handleCustomInputStyle({ style, element: ELEMENTS.CVV });
        break;
      }
      case EVENTS.PAYMENT_METHOD_CHANGED: {
        const { scheme } = e.data.data;
        const oldScheme = this.state.scheme;
        this.state.scheme = scheme;
        validateCVV(oldScheme, scheme, this.clearCVV);
        this.updateMaxInputLength(scheme);
        this.manuallyValidate();
        break;
      }
      case "clear":
        this.state.value = "";
        break;
      case "forceFocus":
        this.input.focus();
        break;
      case "forceBlur":
        this.onBlur();
        break;
      case "autofilled":
        simulateAutofill(this.input, value);
        break;
      default:
        break;
    }
  };

  /**
   * Called to re-validate CVV in case Payment Method is Maestro and empty CVV is allowed
   */
  manuallyValidate = () => {
    this.updateValidationStatus();

    sendToControllerIframe(ELEMENTS.CVV, {
      event: EVENTS.VALUE_CHANGED,
      value: this.state.value,
      validationStatus: this.state.validationStatus,
      previousValidationStatus: this.previousState.validationStatus,
    });
  };

  updateMaxInputLength(scheme) {
    this.input.maxLength = getCVVMaxLength(scheme);
  }

  clearCVV = () => {
    this.state.value = "";
    this.input.value = "";
  };
}

export default Cvv;
